import { logEvent } from "@src/appV2/lib/analytics";
import { useRecentlyVisited } from "@src/appV2/lib/utils/useRecentlyVisited";
import { ADMIN_EVENTS } from "@src/appV2/redesign/assets/constants/firebaseEvents";
import { useSearchAgents } from "@src/appV2/User/api/useSearchAgents";
import { useSearchFacilities } from "@src/appV2/User/api/useSearchFacilities";
import { debounce, type DebouncedFunc } from "lodash";
import { useState } from "react";

import { type AgentDetails, type WorkplaceDetails } from "./types";
import { useHistory } from "react-router-dom";
import { getLocation } from "@src/appV2/redesign/utils/routes";

export const AGENTS_LIMIT_FOR_LOAD = 3;
export const FACILITIES_LIMIT_FOR_LOAD = 3;

export interface AccountSwitcherUser {
  user: {
    userId: string;
    name?: string;
  };
  type: "ADMIN" | "AGENT" | "WORKPLACE";
}

export function useAccountSwitcherHook(
  pseudo: (data: { userId: string }, isFirebaseSession?: boolean) => void,
  closeMenu?: () => void
): {
  handleChange: ({ user, type }: AccountSwitcherUser) => void;
  searchAccounts: DebouncedFunc<(name: string) => void>;
  handleOnDropdownVisible: (visible: boolean) => void;
  accountsListVisible: boolean;
  workplaceDetails: WorkplaceDetails;
  agentDetails: AgentDetails;
  setAccountsListVisible: React.Dispatch<React.SetStateAction<boolean>>;
} {
  const [searchName, setSearchName] = useState<string>();
  const [accountsListVisible, setAccountsListVisible] = useState(false);
  const [, addToRecent] = useRecentlyVisited();
  const history = useHistory();

  const {
    data: searchUsersResponse,
    isLoading: isSearchUsersLoading,
    fetchNextPage: fetchNextPageSearchUsers,
    fetchStatus: fetchStatusSearchUsers,
    isFetchingNextPage: isFetchingNextPageSearchUsers,
    hasNextPage: hasNextPageSearchUsers,
  } = useSearchAgents({
    searchInput: searchName,
    limit: AGENTS_LIMIT_FOR_LOAD,
  });

  const {
    data: searchFacilitiesResponse,
    isLoading: isSearchFacilitiesLoading,
    fetchNextPage: fetchNextPageSearchFacilities,
    fetchStatus: fetchStatusSearchFacilities,
    isFetchingNextPage: isFetchingNextPageSearchFacilities,
    hasNextPage: hasNextPageSearchFacilities,
  } = useSearchFacilities({
    searchInput: searchName ?? "",
    limit: AGENTS_LIMIT_FOR_LOAD,
  });

  const agentDetails: AgentDetails = {
    list:
      searchUsersResponse?.pages.flatMap((page) =>
        page.list.map((item) => ({
          ...item,
          userType: "AGENT",
          id: item._id,
        }))
      ) ?? [],
    loading: isSearchUsersLoading && fetchStatusSearchUsers !== "idle",
    fetchNextPage: fetchNextPageSearchUsers,
    isFetchingNextPage: isFetchingNextPageSearchUsers,
    hasNextPage: hasNextPageSearchUsers,
  };

  const workplaceDetails: WorkplaceDetails = {
    list:
      searchFacilitiesResponse?.pages.flatMap((page) =>
        page.list.map((item) => ({ ...item, userType: "WORKPLACE", id: item._id }))
      ) ?? [],
    loading: isSearchFacilitiesLoading && fetchStatusSearchFacilities !== "idle",
    fetchNextPage: fetchNextPageSearchFacilities,
    isFetchingNextPage: isFetchingNextPageSearchFacilities,
    hasNextPage: hasNextPageSearchFacilities,
  };

  const handleOnDropdownVisible = (visible: boolean) => {
    setAccountsListVisible(visible);
    if (!visible) {
      return;
    }

    logEvent(ADMIN_EVENTS.OPEN_SEARCH);
  };

  const searchAccounts = debounce((name: string) => {
    setSearchName(name);
    logEvent(ADMIN_EVENTS.FILTER_SEARCH, { user_input: name });
  }, 800);

  const handleChange = async ({ user, type }: AccountSwitcherUser) => {
    if (type !== "ADMIN") {
      addToRecent({ id: user.userId, title: user.name ?? "", type });
    }

    if (type === "AGENT") {
      logEvent(ADMIN_EVENTS.SELECT_HCP, {
        id: user.userId,
      });
    } else if (type === "WORKPLACE") {
      logEvent(ADMIN_EVENTS.SELECT_HCF, {
        id: user.userId,
      });
    }

    setAccountsListVisible(false);
    if (closeMenu) {
      closeMenu();
    }

    // Wait for the session to update before reloading
    await pseudo({ userId: user.userId });
    await history.push(getLocation("enter"));

    // Reload the page to ensure all components re-render with new session
    await window.location.reload();
  };

  return {
    handleChange,
    searchAccounts,
    handleOnDropdownVisible,
    accountsListVisible,
    workplaceDetails,
    agentDetails,
    setAccountsListVisible,
  };
}
